import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContractDetailsModal } from '@/features/cancellationDetails/components/ContractDetailsModal';
import { searchContracts } from '@/features/contractSearch/api';
import {
  ContractSearchForm,
  SearchMode,
} from '@/features/contractSearch/components/ContractSearchForm';
import { ExpiredContractDialog } from '@/features/contractSearch/components/ExpiredContractDialog';
import { SearchResultsDisplay } from '@/features/contractSearch/components/SearchResultsDisplay';
import { useContractSelection } from '@/features/contractSearch/hooks/useContractSelection';
import {
  buildCustomerOptionKey,
  useCustomerSelection,
} from '@/features/contractSearch/hooks/useCustomerSelection';
import type {
  ContractDetail,
  ContractSearchByContractNumberRequest,
  ContractSearchByVinRequest,
  ContractSearchRequest,
  ContractSearchResult,
  NameType,
} from '@/features/contractSearch/types';
import { errorService } from '@/lib/errorService';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import oktaAuth from '../features/auth/services';

// Define the expected shape of values from ContractSearchForm's onSubmit
interface SearchFormValues {
  searchMode: SearchMode; // This is guaranteed by the form
  vin?: string;
  customerName?: string;
  contractNumber?: string;
  /** Optional nameType from autocomplete selection for pre-selecting the dropdown option */
  nameType?: NameType;
}

export const Route = createFileRoute('/search')({
  beforeLoad: async ({ location }) => {
    const isAuthenticated = await oktaAuth.isAuthenticated();

    if (!isAuthenticated) {
      // Use location.href which includes query parameters
      oktaAuth.signInWithRedirect({ originalUri: location.href });
      // Still throw redirect to prevent further loading of this route component by TanStack Router
      throw redirect({
        to: '/',
      });
    }

    return {};
  },
  component: SearchComponent,
});

function SearchComponent() {
  const navigate = useNavigate();
  const [searchApiParams, setSearchApiParams] =
    useState<ContractSearchRequest | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  const [selectedContractForDetails, setSelectedContractForDetails] =
    useState<ContractDetail | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Track the searched name type and name from autocomplete for pre-selection
  const [searchedNameType, setSearchedNameType] = useState<
    NameType | undefined
  >();
  const [searchedName, setSearchedName] = useState<string | undefined>();

  const {
    data,
    isLoading,
    isError,
    error: queryError,
  } = useQuery<ContractSearchResult, Error>({
    queryKey: ['contracts', searchApiParams, refetchTrigger],
    queryFn: () => {
      if (!searchApiParams) {
        return Promise.reject(new Error('No search parameters provided.'));
      }
      return searchContracts(searchApiParams);
    },
    enabled: !!searchApiParams,
  });

  useEffect(() => {
    if (isError && queryError) {
      toast.error(queryError.message);
    }
  }, [isError, queryError]);

  // Customer selection logic with searchedNameType for pre-selection
  const customerSelection = useCustomerSelection({
    data,
    searchedNameType,
    searchedName,
  });

  // Contract selection logic with customer selection data (initialize with empty values)
  const contractSelection = useContractSelection({
    displayedContracts: customerSelection.displayedContracts,
    shouldShowCustomerSelection: customerSelection.shouldShowCustomerSelection,
    selectedContractCustomers: [], // Deprecated - using selectedContractOptionKeys instead
  });

  // Calculate unique customer option keys for currently selected contracts.
  // Returns option keys (matchedName|matchedNameType) to match the dropdown options.
  const selectedContractOptionKeys = useMemo(() => {
    if (
      !data ||
      data.status !== 'success' ||
      !contractSelection.selectedContracts.size
    ) {
      return [];
    }

    // Collect unique option keys for selected contracts
    const optionKeys = new Set<string>();
    const missingContracts: string[] = [];

    contractSelection.selectedContracts.forEach((contractNumber) => {
      const contract = data.contracts.find(
        (c) => c.contractNumber === contractNumber
      );

      if (!contract) {
        missingContracts.push(contractNumber);
        return;
      }

      // Build the key using the shared helper function for consistency
      const matchedName = contract.matchedName || contract.buyerName;
      const matchedNameType = contract.matchedNameType || 'buyer';
      const key = buildCustomerOptionKey(matchedName, matchedNameType);
      optionKeys.add(key);
    });

    // Report data quality issues
    if (missingContracts.length > 0) {
      errorService.reportError(
        new Error('Selected contracts not found in data'),
        {
          component: 'SearchComponent',
          location: 'selectedContractOptionKeys',
          missingContracts,
        }
      );
    }

    return Array.from(optionKeys);
  }, [data, contractSelection.selectedContracts]);

  // Auto-select customer option when all selected contracts belong to one option
  useEffect(() => {
    if (
      selectedContractOptionKeys.length === 1 &&
      !customerSelection.selectedCustomer
    ) {
      const autoSelectedOptionKey = selectedContractOptionKeys[0];
      customerSelection.handleCustomerSelectionChange(autoSelectedOptionKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The individual properties of customerSelection are stable, but a new object is created each time the hook is called
  }, [
    selectedContractOptionKeys,
    customerSelection.selectedCustomer,
    customerSelection.handleCustomerSelectionChange,
  ]);

  // Handle customer change to auto-deselect contracts from other customers
  const handleCustomerSelectionChange = (optionKey: string) => {
    customerSelection.handleCustomerSelectionChange(optionKey);
    contractSelection.clearValidation();

    // Auto-deselect contracts that don't belong to the newly selected customer option
    if (data && data.status === 'success') {
      // Find the selected option from uniqueCustomers
      const selectedOption = customerSelection.uniqueCustomers.find(
        (opt) => opt.key === optionKey
      );

      if (!selectedOption) {
        // This indicates a data inconsistency - the selected option should exist
        errorService.reportError(
          new Error('Cannot find customer option in available options'),
          {
            component: 'SearchComponent',
            handler: 'handleCustomerSelectionChange',
            selectedKey: optionKey,
            availableKeys: customerSelection.uniqueCustomers.map((o) => o.key),
          }
        );

        toast.error(
          'Unable to filter by selected customer. Please try searching again or contact support if the problem persists.'
        );

        contractSelection.clearSelections();
        return;
      }

      const contractsToKeep: string[] = [];
      const contractsNotFound: string[] = [];

      Array.from(contractSelection.selectedContracts).forEach(
        (contractNumber) => {
          const contract = data.contracts.find(
            (c) => c.contractNumber === contractNumber
          );

          if (!contract) {
            contractsNotFound.push(contractNumber);
            return;
          }

          // Match contracts by matchedName + matchedNameType (VIN search)
          // or by buyerName (contract number search fallback)
          // Use case-insensitive comparison for consistency with filtering logic
          const contractMatches =
            contract.matchedName && contract.matchedNameType
              ? contract.matchedName.toLowerCase() ===
                  selectedOption.matchedName.toLowerCase() &&
                contract.matchedNameType === selectedOption.matchedNameType
              : contract.buyerName.toLowerCase() ===
                selectedOption.buyerName.toLowerCase();

          if (contractMatches) {
            contractsToKeep.push(contractNumber);
          }
        }
      );

      // Report when contracts are not found (potential data integrity issue)
      if (contractsNotFound.length > 0) {
        errorService.reportError(
          new Error('Selected contracts not found in current data'),
          {
            component: 'SearchComponent',
            handler: 'handleCustomerSelectionChange',
            contractsNotFound,
          }
        );
      }

      // Clear and re-add only the contracts that belong to the selected customer option
      contractSelection.clearSelections();
      contractsToKeep.forEach((contractNumber) => {
        contractSelection.handleContractSelectionChange(contractNumber, true);
      });
    }
  };

  const handleSearchModeChange = () => {
    // Clear search results and selections when search mode changes
    setSearchApiParams(null);
    contractSelection.clearSelections();
    customerSelection.clearCustomerSelection();
  };

  const handleSearchSubmit = (values: SearchFormValues) => {
    // Clear selections on new search
    contractSelection.clearSelections();
    customerSelection.clearCustomerSelection();

    // Store nameType and name for pre-selecting dropdown option
    setSearchedNameType(values.nameType);
    setSearchedName(values.customerName);

    let params: ContractSearchRequest;
    if (values.searchMode === 'vinAndName') {
      // Ensure vin and customerName are provided, as form validation should handle this
      if (!values.vin || !values.customerName) {
        return;
      }
      params = {
        searchType: 'vin',
        vin: values.vin,
        customerName: values.customerName,
        // Include nameType for frontend pre-selection (backend returns ALL matches regardless)
        nameType: values.nameType,
      } as ContractSearchByVinRequest;
    } else {
      // Ensure contractNumber is provided
      if (!values.contractNumber) {
        errorService.reportError(
          new Error('Contract Number is required for this search mode.'),
          {
            component: 'SearchComponent',
            handler: 'handleSearchSubmit',
            searchMode: values.searchMode,
          }
        );
        return;
      }
      params = {
        searchType: 'contractNumber',
        contractNumber: values.contractNumber,
      } as ContractSearchByContractNumberRequest;
    }

    setSearchApiParams(params);

    // Always refetch to ensure fresh results
    setRefetchTrigger((prev) => prev + 1);
  };

  const handleRequestQuotes = () => {
    // Attempt validation first - ensure selected contracts belong to one customer option
    const hasMultipleOptions =
      customerSelection.shouldShowCustomerSelection &&
      selectedContractOptionKeys.length > 1;

    if (hasMultipleOptions) {
      // Set validation attempted to show error message
      contractSelection.attemptValidation();
      return;
    }

    if (data && data.status === 'success' && data.contracts) {
      const contractIdArray = Array.from(contractSelection.selectedContracts);

      // Filter the full contract objects for those selected
      const selectedContractObjects: ContractDetail[] = data.contracts.filter(
        (contract) =>
          contractSelection.selectedContracts.has(contract.contractNumber)
      );

      // Check if any selected contracts have "Expired" status
      const hasExpiredContracts = selectedContractObjects.some(
        (contract) => contract.status === 'Expired'
      );

      if (hasExpiredContracts) {
        setShowExpiredDialog(true);
        return;
      }

      navigate({
        to: '/request-quote',
        search: {
          contracts: contractIdArray,
        },
        state: {
          selectedContracts: selectedContractObjects,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
    }
  };

  // Determine validation error message when selected contracts span multiple customer options
  const validationErrorMessage = useMemo(() => {
    if (!contractSelection.validationAttempted) {
      return null;
    }

    if (
      customerSelection.shouldShowCustomerSelection &&
      selectedContractOptionKeys.length > 1
    ) {
      return 'Please select contracts from only one customer, or choose a specific customer to filter the results.';
    }

    return null;
  }, [
    contractSelection.validationAttempted,
    customerSelection.shouldShowCustomerSelection,
    selectedContractOptionKeys.length,
  ]);

  const handleExpiredContractConfirm = () => {
    // Proceed with navigation after user acknowledges the warning
    if (data && data.status === 'success' && data.contracts) {
      const contractIdArray = Array.from(contractSelection.selectedContracts);
      const selectedContractObjects: ContractDetail[] = data.contracts.filter(
        (contract) =>
          contractSelection.selectedContracts.has(contract.contractNumber)
      );

      navigate({
        to: '/request-quote',
        search: {
          contracts: contractIdArray,
        },
        state: {
          selectedContracts: selectedContractObjects,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
    }
  };

  const handleViewDetails = (contract: ContractDetail) => {
    setSelectedContractForDetails(contract);
    setShowDetailsModal(true);
  };

  return (
    <div className='container mx-auto p-4 space-y-6'>
      <h1 className='text-2xl font-semibold'>Contract Search</h1>

      <ContractSearchForm
        onSubmit={handleSearchSubmit}
        isSubmitting={isLoading}
        onSearchModeChange={handleSearchModeChange}
      />

      {/* Customer Selection - only show when multiple customers exist */}
      {customerSelection.shouldShowCustomerSelection &&
        data &&
        data.status === 'success' && (
          <div className='space-y-2'>
            <Label htmlFor='customer-select' className='text-sm font-medium'>
              Select Customer
            </Label>
            <Select
              value={customerSelection.selectedCustomer}
              onValueChange={handleCustomerSelectionChange}
            >
              <SelectTrigger id='customer-select' className='w-full max-w-md'>
                <SelectValue placeholder='Choose a customer to view their contracts' />
              </SelectTrigger>
              <SelectContent>
                {customerSelection.uniqueCustomers.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.displayLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Show validation error message */}
            {validationErrorMessage && (
              <p className='text-destructive text-sm'>
                {validationErrorMessage}
              </p>
            )}
          </div>
        )}

      {/* Context banner explaining the current filter */}
      {customerSelection.selectedOption &&
        customerSelection.shouldShowCustomerSelection && (
          <Card className='bg-accent/5 border-accent/20'>
            <CardContent>
              <div className='text-foreground'>
                Showing contracts where &ldquo;
                {customerSelection.selectedOption.matchedName}&rdquo; is the{' '}
                {customerSelection.selectedOption.matchedNameType} name.
              </div>
            </CardContent>
          </Card>
        )}

      <div aria-live='polite' aria-atomic='true' className='mt-6'>
        {isLoading && <p>Searching for contracts...</p>}
        {isError && (
          <p className='text-destructive'>
            Error searching contracts:{' '}
            {queryError?.message || 'An unknown error occurred.'}
          </p>
        )}
        {data && (
          <SearchResultsDisplay
            data={data}
            selectedContracts={contractSelection.selectedContracts}
            onContractSelectionChange={
              contractSelection.handleContractSelectionChange
            }
            isAllOrNoneSelected={contractSelection.selectionStatus}
            displayedContracts={customerSelection.displayedContracts}
            onViewDetails={handleViewDetails}
          />
        )}
      </div>

      {data &&
        data.status === 'success' &&
        customerSelection.displayedContracts.length > 0 && (
          <div className='mt-6 flex justify-end'>
            <Button
              onClick={handleRequestQuotes}
              disabled={contractSelection.selectedContracts.size === 0}
            >
              Request Quotes for Selected (
              {contractSelection.selectedContracts.size})
            </Button>
          </div>
        )}

      {/* Expired Contracts Dialog */}
      <ExpiredContractDialog
        open={showExpiredDialog}
        onOpenChange={setShowExpiredDialog}
        onConfirm={handleExpiredContractConfirm}
      />

      {/* Contract Details Modal */}
      {selectedContractForDetails && (
        <ContractDetailsModal
          open={showDetailsModal}
          onOpenChange={setShowDetailsModal}
          contractNumber={selectedContractForDetails.contractNumber}
          customerName={selectedContractForDetails.customerFullName}
          vehicleInfo={selectedContractForDetails.vehicleInfo}
          status={selectedContractForDetails.status}
        />
      )}
    </div>
  );
}
